import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  GetCrewsParams,
  GetTasksParams,
} from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import { DiaryType } from "Components/DiaryCalendar";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { PdfReport } from "Models/pdf.report";
import { ReportsDispatchService } from "Services/reports.dispatch.service";
import { DispatchReportType, WarehouseEquipmentType } from "Util/enums";
import { getIntervalByDiaryType } from "Util/getIntervalByDiaryType";
import { UserPreferences } from "Models/user.preferences";

type GenerateCrewScheduleReport = GetCrewsParams & {
  reportType: DispatchReportType.CREW_SCHEDULE_REPORT;
  viewMode: DiaryType;
  timezone: string;
  preferences: UserPreferences;
};

type GenerateWarehouseScheduleReport = GetTasksParams & {
  reportType: DispatchReportType.WAREHOUSE_SCHEDULE_REPORT;
  timezone: string;
  equipmentType?: WarehouseEquipmentType;
  preferences: UserPreferences;
};

type GenerateDispatchReports = GenerateCrewScheduleReport | GenerateWarehouseScheduleReport;

export const useGenerateDispatchReportsActions = () => {
  const useGenerateReport = (
    onSuccess: (response: PdfReport[], variables: GenerateDispatchReports) => void
  ) => {
    return useMutation({
      mutationKey: ["generateDispatchPDF"],
      mutationFn: async (variables: GenerateDispatchReports) => {
        const {
          dateSelected = new Date(),
          companyId,
          branchId,
          reportType,
          viewMode,
          timezone,
          equipmentType,
          preferences,
        } = variables as GenerateDispatchReports & {
          equipmentType?: WarehouseEquipmentType;
          preferences: UserPreferences;
        };

        const { startDateISO, endDateISO } = getIntervalByDiaryType(dateSelected, viewMode);

        const filters = {
          companyId,
          branchId,
          startDate: startDateISO,
          endDate: endDateISO,
          reportType,
          timezone,
          preferences,
          ...(equipmentType ? { equipmentType } : {}),
        };

        return ReportsDispatchService.generatePdf(filters);
      },
      onSuccess: (response, variables) => {
        onSuccess(response.data, variables);
      },
      onError: (error: AxiosError<Error>) => {
        if (error?.response) {
          NotificationToast.error(error.response.data?.message);
        }
      },
    });
  };

  return {
    useGenerateReport,
  };
};
